import { nanoid } from 'nanoid'
import { MediaImageRepositoryProcessingOffscreen } from '@/shared/MediaImageRepositoryProcessingOffscreen'
import {
  INDEXED_IMAGE_ACTIONS,
  INDEXED_IMAGE_TRANSFER_QUERY_PARAM,
  saveIndexedImage,
} from '@/utils/indexedImage'
import { OpenImageSource, openBlankEditorTab } from '@/utils/openImageSourceInEditor'

/** Marks an editor tab as a single-image batch refine session. */
export const BATCH_REFINE_QUERY_PARAM = 'batchRefine'
/** Carries the batch item id so the editor can route the result back. */
export const BATCH_REFINE_ITEM_QUERY_PARAM = 'batchRefineItemId'
/** IndexedDB transfer id for a prior cutout, set when reopening refine on an already-erased item. */
export const BATCH_REFINE_CUTOUT_QUERY_PARAM = 'batchRefineCutoutId'
/**
 * localStorage key the editor sets to notify the batch tab a refined result is
 * ready. Same-origin `storage` events fire in the other tab; the batch tab also
 * scans on focus/visibility as a Safari-safe fallback.
 */
export const BATCH_REFINE_RESULT_PING_KEY = 'batchRefineResultReady'

/**
 * Opens the editor in a new tab to refine a batch item's cutout with the Eraser.
 *
 * Transfers the ORIGINAL (opaque) image rather than the flat cutout: the editor
 * re-runs its own background removal so erase / restore have the original pixels
 * and a proper mask to work with. Safari: the tab is opened synchronously inside
 * the user gesture before any `await`.
 */
export async function openBatchRefineInEditor(
  originalSource: OpenImageSource,
  itemId: string,
  priorCutoutSource?: OpenImageSource | null,
): Promise<void> {
  const editorTab = openBlankEditorTab()
  try {
    const imageProcessingOffscreen = new MediaImageRepositoryProcessingOffscreen()
    const blob = await imageProcessingOffscreen.toBlob(originalSource)
    const transferId = nanoid()
    await saveIndexedImage(blob, INDEXED_IMAGE_ACTIONS.OPEN_EDITOR_BATCH, transferId)
    const openUrl = new URL(window.location.origin)
    openUrl.searchParams.set(INDEXED_IMAGE_TRANSFER_QUERY_PARAM, transferId)
    openUrl.searchParams.set(BATCH_REFINE_QUERY_PARAM, '1')
    openUrl.searchParams.set(BATCH_REFINE_ITEM_QUERY_PARAM, itemId)

    if (priorCutoutSource) {
      const cutoutBlob = await imageProcessingOffscreen.toBlob(priorCutoutSource)
      const cutoutTransferId = nanoid()
      await saveIndexedImage(cutoutBlob, INDEXED_IMAGE_ACTIONS.OPEN_EDITOR_BATCH, cutoutTransferId)
      openUrl.searchParams.set(BATCH_REFINE_CUTOUT_QUERY_PARAM, cutoutTransferId)
    }

    if (editorTab) {
      editorTab.location.href = openUrl.toString()
    } else {
      window.open(openUrl.toString(), '_blank')
    }
  } catch (error) {
    editorTab?.close()
    throw error
  }
}

/**
 * Editor -> batch notification. Writing to localStorage triggers a same-origin
 * `storage` event in the batch tab (fast path). Wrapped in try/catch because
 * setItem throws in private mode / when quota is exceeded; the focus fallback
 * still picks the result up in that case.
 */
export function pingBatchRefineResult(itemId: string): void {
  try {
    window.localStorage.setItem(BATCH_REFINE_RESULT_PING_KEY, `${itemId}:${Date.now()}`)
  } catch {
    // Ignored — focus/visibility fallback handles delivery.
  }
}
